import { defineComponent as _defineComponent } from 'vue'
import GmapCluster from 'vue2-google-maps/dist/components/cluster';
import { store } from '@/store';
import MarkerIcon from './MarkerIcon';
import MapStyles from './map-styles';
import ClusterStyles from './cluster-styles';
import StoreLocatorListItem from './StoreLocatorListItem.vue';
import StoreLocatorMapItem from './StoreLocatorMapItem.vue';
import StoreLocatorMapZeroResults from './StoreLocatorMapZeroResults.vue';
import { computed, onMounted, ref } from 'vue';
import dictionaryService from '@/core/dictionary.service';

// see options here https://developers.google.com/maps/documentation/javascript/places-autocomplete#add_autocomplete

enum View {
    Map,
    List,
}

interface IAutocompleteOptions {
    types?: any;
    bounds?: google.maps.LatLngBounds;
    strictBounds?: boolean;
}

interface IFilterOption {
    key: string;
    name: string;
    enabled: boolean;
    show: boolean;
    label: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'StoreLocator',
  props: {
    locations: null,
    headline: null,
    showCertifiedFilter: { type: Boolean },
    defaultMapSection: null,
    disableFilters: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
    locations?: IMapStoreLocation[],
    headline?: string,
    showCertifiedFilter?: boolean,
    defaultMapSection?: MapSection,
    disableFilters?: boolean
}

/// <reference types="@types/googlemaps" />
const theme = computed(() => store.getters['app/theme']);
const borderColor = computed<OpacityFunction>(() => store.getters['app/borderColor']);
const textColor = computed<OpacityFunction>(() => store.getters['app/textColor']);


const defaultMapCenter = {
    lat: parseFloat(props.defaultMapSection.lat),
    lng: parseFloat(props.defaultMapSection.lng)
};

const defaultMapZoom = parseFloat(props.defaultMapSection.zoom);

const viewMap = View.Map;
const viewList = View.List;

const view = ref<View>(View.Map);
const mapCenter = ref<any>(defaultMapCenter);
const mapZoom = ref<number>(defaultMapZoom || 13);
const overlay = ref<any>(null);

const bounds = ref<google.maps.LatLngBounds>(null);
const clickedLocation = ref<IMapStoreLocation>(null);
const defaultLocationsToRenderIncrementation = 10;
const locationsCountToRender = ref<number>(defaultLocationsToRenderIncrementation);
const autocompleteOptions: IAutocompleteOptions = {
    types: ['geocode'],
    strictBounds: false
};

const gmap = ref(null);

onMounted(() => {
    setTimeout(() => {
        (gmap.value as any).$mapPromise.then((map: google.maps.Map) => {
            setupOverlay(map);
            setUpZoomControls(map);
        });
    }, 100);
});
function setupOverlay(map: google.maps.Map) {
    class Overlay extends google.maps.OverlayView {
        div: HTMLDivElement;
        constructor(map) {
            super();
            this.setMap(map);
        }

        onAdd() {
            this.div = document.getElementById('map-overlay') as HTMLDivElement;
            const panes = this.getPanes()!;
            panes.overlayLayer.parentNode.parentNode.appendChild(this.div);
        }

        onRemove() {
            if (this.div) {
                (this.div.parentNode as HTMLElement).removeChild(this.div);
                delete this.div;
            }
        }

        hide() {
            if (this.div) {
                this.div.classList.add('u-opacity-0');
                setTimeout(() => this.div.classList.remove('u-z-100'), 200);
            }
        }

        show() {
            if (this.div) {
                this.div.classList.remove('u-opacity-0');
                this.div.classList.add('u-z-100');
            }
        }

        toggle() {
            if (this.div) {
                if (this.div.classList.contains('u-opacity-0')) {
                    this.show();
                } else {
                    this.hide();
                }
            }
        }
    }
    overlay.value = new Overlay(map);
}
function setUpZoomControls(map: google.maps.Map) {
    const zoomControlDiv = document.getElementById('zoom-controls');

    document.getElementById('zoom-in-button').addEventListener('click', function() {
        map.setZoom(map.getZoom() + 1);
    });
    document.getElementById('zoom-out-button').addEventListener('click', function() {
        map.setZoom(map.getZoom() - 1);
    });

    map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomControlDiv);
}

const markerIcon = MarkerIcon;
const mapOptions = {
    disableDefaultUI: true,
    zoomControl: false,
    styles: MapStyles
};
const clusterStyles = ClusterStyles;

/**
 * functions
 */
function toggleView(newView: View): void {
    view.value = newView;
    locationsCountToRender.value = defaultLocationsToRenderIncrementation; // Reset count to default
}

function mapIdle(): void {
    (gmap.value as any).$mapPromise.then((map: google.maps.Map) => {
        bounds.value = map.getBounds();
        if (overlay.value) {
            if (!newLocations.value.length) {
                clickedLocation.value = null;
                overlay.value.show();
            } else {
                overlay.value.hide();
            }
        }
    });
}

function toggleInfoWindow(location: IMapStoreLocation): void {
    clickedLocation.value = location;
}

function autocompleteEvent(input: google.maps.places.PlaceResult): void {
    if ('geometry' in input === false) {
        return;
    }
    (gmap.value as any).$mapPromise.then((map: google.maps.Map) => {
        if (input.geometry) {
            const newBounds = input.geometry.viewport;
            const closestLocationPosition = getClosestLocation(input.geometry.location);
            newBounds.extend(input.geometry.location);
            newBounds.extend(closestLocationPosition);
            map.fitBounds(newBounds);
        }
    });
}

function getClosestLocation(origin) {
    const result = props.locations.reduce(
        (closest: any, location, index) => {
            const position = new google.maps.LatLng(parseFloat(location.lat), parseFloat(location.lng));
            const distance = google.maps.geometry.spherical.computeDistanceBetween(origin, position);
            if (closest.distance === -1 || distance < closest.distance) {
                return {
                    distance,
                    position
                };
            }
            return closest;
        },
        {
            distance: -1
        }
    );
    return result.position;
}

function filterCount(key) {
    return newLocations.value.reduce((count, location) => {
        if (location[key]) {
            return count + 1;
        }
        return count;
    }, 0);
}

function increaseLocationsToShow() {
    locationsCountToRender.value += defaultLocationsToRenderIncrementation;
}

const filterOptions: FilterToggleOption[] =
    [
        { text: dictionaryService.get('Pages.StoreLocator.MapLabel'), value: viewMap },
        { text: dictionaryService.get('Pages.StoreLocator.ListLabel'), value: viewList }
    ];

/**
 * computed properties
 */
const filters = ref<IFilterOption[]>([{
    key: 'paleRoseExclusiveDealer',
    name: 'PaleRoseExclusiveDealer',
    enabled: false,
    show: true,
    label: 'Pages.StoreLocator.FilterPaleRoseExclusiveDealer'
}]);

const inViewCount = computed(() => {
    return newLocations.value.length;
});

const locationsToRender = computed(() => {
    return newLocations.value.slice(0, locationsCountToRender.value);
});

const newLocations = computed(() => {
    if (!bounds.value) {
        return [];
    }
    const locations = props.locations
        .filter(location => {
            const position = {
                lat: parseFloat(location.lat),
                lng: parseFloat(location.lng)
            };
            const visible = !filters.value.some(({ key, enabled }) => enabled && location[key] === false);
            return visible && bounds.value.contains(position);
        })
        .map((location, id) => {
            const position = {
                lat: parseFloat(location.lat),
                lng: parseFloat(location.lng)
            };
            return {
                ...location,
                id,
                position,
                distance: 0
            };
        })
        .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    return locations;
});


return { __sfc: true,View, theme, borderColor, textColor, props, defaultMapCenter, defaultMapZoom, viewMap, viewList, view, mapCenter, mapZoom, overlay, bounds, clickedLocation, defaultLocationsToRenderIncrementation, locationsCountToRender, autocompleteOptions, gmap, setupOverlay, setUpZoomControls, markerIcon, mapOptions, clusterStyles, toggleView, mapIdle, toggleInfoWindow, autocompleteEvent, getClosestLocation, filterCount, increaseLocationsToShow, filterOptions, filters, inViewCount, locationsToRender, newLocations, GmapCluster, StoreLocatorListItem, StoreLocatorMapItem, StoreLocatorMapZeroResults }
}

})